// ===========================================
// Auth Context - Global Authentication State
// ===========================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

// Create the context
const AuthContext = createContext(null);

// ===========================================
// Auth Provider Component
// ===========================================
export const AuthProvider = ({ children }) => {
  // State for user and loading
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ===========================================
  // Initialize auth state from localStorage or sessionStorage
  // ===========================================
  // Checks localStorage first (remembered sessions), then sessionStorage (temporary sessions)
  useEffect(() => {
    const initAuth = async () => {
      // Check localStorage first (persistent/remembered), then sessionStorage (temporary)
      let token = localStorage.getItem('devpad_token');
      let savedUser = localStorage.getItem('devpad_user');
      let activeStorage = localStorage;
      
      // If not found in localStorage, check sessionStorage
      if (!token) {
        token = sessionStorage.getItem('devpad_token');
        savedUser = sessionStorage.getItem('devpad_user');
        activeStorage = sessionStorage;
      }
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data.data.user);
          // Update user data in the appropriate storage
          activeStorage.setItem('devpad_user', JSON.stringify(response.data.data.user));
        } catch (err) {
          // Token invalid, clear both storages to be safe
          localStorage.removeItem('devpad_token');
          localStorage.removeItem('devpad_user');
          localStorage.removeItem('devpad_remember');
          sessionStorage.removeItem('devpad_token');
          sessionStorage.removeItem('devpad_user');
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  // ===========================================
  // Helper: Get active storage based on remember preference
  // ===========================================
  // Returns the storage where user data is stored (localStorage or sessionStorage)
  const getActiveStorage = useCallback(() => {
    // Check if user is in localStorage (remembered) or sessionStorage (temporary)
    if (localStorage.getItem('devpad_token')) {
      return localStorage;
    }
    return sessionStorage;
  }, []);

  // ===========================================
  // Register new user
  // ===========================================
  // New registrations default to localStorage (remembered) for best experience
  const register = useCallback(async (email, password, name) => {
    setError(null);
    setLoading(true);
    
    try {
      const response = await authAPI.register({ email, password, name });
      const { user: userData, token } = response.data.data;
      
      // Save to localStorage (new registrations are always remembered)
      localStorage.setItem('devpad_token', token);
      localStorage.setItem('devpad_user', JSON.stringify(userData));
      localStorage.setItem('devpad_remember', 'true');
      
      setUser(userData);
      setLoading(false);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Registration failed';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Login user
  // ===========================================
  // rememberMe: true = localStorage (persists), false = sessionStorage (cleared on browser close)
  const login = useCallback(async (email, password, rememberMe = true) => {
    setError(null);
    setLoading(true);
    
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, token } = response.data.data;
      
      // Choose storage based on rememberMe preference
      // localStorage persists across browser sessions, sessionStorage clears on close
      const storage = rememberMe ? localStorage : sessionStorage;
      
      // Clear the other storage to avoid conflicts/stale data
      if (rememberMe) {
        sessionStorage.removeItem('devpad_token');
        sessionStorage.removeItem('devpad_user');
      } else {
        localStorage.removeItem('devpad_token');
        localStorage.removeItem('devpad_user');
      }
      
      // Save to appropriate storage
      storage.setItem('devpad_token', token);
      storage.setItem('devpad_user', JSON.stringify(userData));
      
      // Store the preference in localStorage so we know which storage to check
      localStorage.setItem('devpad_remember', rememberMe.toString());
      
      setUser(userData);
      setLoading(false);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Login failed';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Google OAuth Login
  // ===========================================
  // Authenticates using Google ID token from Google Identity Services
  const googleLogin = useCallback(async (idToken, rememberMe = true) => {
    setError(null);
    setLoading(true);
    
    try {
      // Send Google ID token to backend for verification
      const response = await authAPI.googleAuth({ idToken });
      const { user: userData, token } = response.data.data;
      
      // Choose storage based on rememberMe preference
      const storage = rememberMe ? localStorage : sessionStorage;
      
      // Clear the other storage to avoid conflicts/stale data
      if (rememberMe) {
        sessionStorage.removeItem('devpad_token');
        sessionStorage.removeItem('devpad_user');
      } else {
        localStorage.removeItem('devpad_token');
        localStorage.removeItem('devpad_user');
      }
      
      // Save to appropriate storage
      storage.setItem('devpad_token', token);
      storage.setItem('devpad_user', JSON.stringify(userData));
      localStorage.setItem('devpad_remember', rememberMe.toString());
      
      setUser(userData);
      setLoading(false);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Google login failed';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // ===========================================
  // Logout user
  // ===========================================
  // Clears both storages to ensure complete logout regardless of remember preference
  const logout = useCallback(() => {
    // Clear localStorage (persistent storage)
    localStorage.removeItem('devpad_token');
    localStorage.removeItem('devpad_user');
    localStorage.removeItem('devpad_remember');
    // Clear sessionStorage (temporary storage)
    sessionStorage.removeItem('devpad_token');
    sessionStorage.removeItem('devpad_user');
    setUser(null);
    setError(null);
  }, []);

  // ===========================================
  // Update user profile
  // ===========================================
  const updateProfile = useCallback(async (updates) => {
    setError(null);
    
    try {
      const response = await authAPI.updateProfile(updates);
      const updatedUser = response.data.data.user;
      
      // Save to the active storage (whichever one has the token)
      getActiveStorage().setItem('devpad_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Update failed';
      setError(message);
      return { success: false, error: message };
    }
  }, [getActiveStorage]);

  // ===========================================
  // Change password
  // ===========================================
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null);
    
    try {
      const response = await authAPI.changePassword({ currentPassword, newPassword });
      
      // Update token if provided in the active storage
      if (response.data.data.token) {
        getActiveStorage().setItem('devpad_token', response.data.data.token);
      }
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Password change failed';
      setError(message);
      return { success: false, error: message };
    }
  }, [getActiveStorage]);

  // ===========================================
  // Clear error
  // ===========================================
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ===========================================
  // Refresh User Data
  // ===========================================
  // Fetches latest user data from server and updates local state
  // Used after operations that modify user data (subscriptions, cursor usage, etc.)
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data.data.user;
      
      // Update the active storage and state with fresh user data
      getActiveStorage().setItem('devpad_user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return { success: false, error: 'Failed to refresh user data' };
    }
  }, [getActiveStorage]);

  // ===========================================
  // Subscription Management (Global Dev Costs)
  // ===========================================
  // These are shared costs like Cursor, ChatGPT, Vercel, etc.

  // Add a new subscription
  const addSubscription = useCallback(async (subscriptionData) => {
    setError(null);
    
    try {
      const response = await authAPI.addSubscription(subscriptionData);
      const updatedSubscriptions = response.data.data.subscriptions;
      
      // Update user state with new subscriptions
      const updatedUser = { ...user, globalSubscriptions: updatedSubscriptions };
      getActiveStorage().setItem('devpad_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true, subscription: response.data.data.subscription };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to add subscription';
      setError(message);
      return { success: false, error: message };
    }
  }, [user, getActiveStorage]);

  // Update an existing subscription
  const updateSubscription = useCallback(async (subscriptionId, updates) => {
    setError(null);
    
    try {
      const response = await authAPI.updateSubscription(subscriptionId, updates);
      const updatedSubscriptions = response.data.data.subscriptions;
      
      // Update user state with new subscriptions
      const updatedUser = { ...user, globalSubscriptions: updatedSubscriptions };
      getActiveStorage().setItem('devpad_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to update subscription';
      setError(message);
      return { success: false, error: message };
    }
  }, [user, getActiveStorage]);

  // Delete a subscription
  const deleteSubscription = useCallback(async (subscriptionId) => {
    setError(null);
    
    try {
      const response = await authAPI.deleteSubscription(subscriptionId);
      const updatedSubscriptions = response.data.data.subscriptions;
      
      // Update user state with new subscriptions
      const updatedUser = { ...user, globalSubscriptions: updatedSubscriptions };
      getActiveStorage().setItem('devpad_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to delete subscription';
      setError(message);
      return { success: false, error: message };
    }
  }, [user, getActiveStorage]);

  // ===========================================
  // Cursor Usage Management (AI Dev Token Costs)
  // ===========================================
  // Variable costs from Cursor billing CSV uploads
  // Now stores individual events for historical tracking

  // Upload Cursor billing CSV data (merges with existing history)
  const uploadCursorUsage = useCallback(async (csvData) => {
    setError(null);
    
    try {
      const response = await authAPI.uploadCursorUsage(csvData);
      const { totalProcessed, newEvents, duplicatesSkipped, summary } = response.data.data;
      
      // Update user's lastUploadedAt timestamp in local state
      const updatedUser = { 
        ...user, 
        cursorUsage: { 
          ...user?.cursorUsage,
          lastUploadedAt: new Date().toISOString()
        }
      };
      getActiveStorage().setItem('devpad_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { 
        success: true, 
        data: { totalProcessed, newEvents, duplicatesSkipped, summary } 
      };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Failed to upload Cursor usage';
      setError(message);
      return { success: false, error: message };
    }
  }, [user, getActiveStorage]);

  // Context value - all auth-related state and methods
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    // Auth methods
    register,
    login,
    googleLogin,  // Google OAuth login
    logout,
    updateProfile,
    changePassword,
    clearError,
    refreshUser,  // Refresh user data from server
    // Subscription methods
    addSubscription,
    updateSubscription,
    deleteSubscription,
    // Cursor usage methods
    uploadCursorUsage
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ===========================================
// Custom hook for using auth context
// ===========================================
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;

